import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Loader2, MoreHorizontal } from 'lucide-react';

/** One thing that can be done to an ONU, described once and rendered twice. */
export interface OnuAction {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  title?: string;
  loading?: boolean;
  /** Destructive, so it is coloured for it and never promoted to the bar. */
  danger?: boolean;
  /**
   * Restores something — coloured green, and still promotable.
   *
   * The platform's colour language pairs the two: red disables, green enables.
   * Without this the Enable that replaces a red Disable would come back in the
   * neutral secondary, and the pair would read as one warning and one nothing.
   */
  success?: boolean;
}

/** How many fit across a phone before the targets get too narrow to hit. */
const INLINE = 3;

/**
 * The ONU's actions as a footer bar, on phones only.
 *
 * On a desktop these sit beside the page title, where there is room for eight
 * buttons. On a phone that same row is most of the screen, above the reading an
 * engineer opened the page for — so here they take the shape a phone already
 * uses for its own navigation: icon over label, spread across the width, fixed
 * to the bottom and within reach of a thumb.
 *
 * Three are shown and the rest live behind More. Squeezing eight into a phone's
 * width gives every one of them a target too narrow to hit reliably, and one of
 * those eight deletes a subscriber's service. Delete is never promoted to the
 * bar for the same reason: the most destructive action stays a deliberate
 * second tap.
 */
export function OnuActionBar({ actions }: { actions: OnuAction[] }) {
  const [open, setOpen] = useState(false);
  const sheet = useRef<HTMLDivElement>(null);

  const promotable = actions.filter((a) => !a.danger);
  const inline = promotable.slice(0, INLINE);
  const overflow = actions.filter((a) => !inline.includes(a));

  // Tapping the page behind the sheet closes it, which is what a sheet does.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!sheet.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  if (actions.length === 0) return null;

  const run = (action: OnuAction) => {
    setOpen(false);
    action.onClick();
  };

  return (
    <>
      {/* The page ends above the bar rather than behind it. */}
      <div className="h-20 sm:hidden" aria-hidden />

      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 sm:hidden" aria-hidden />
      )}

      <div className="fixed inset-x-0 bottom-0 z-40 sm:hidden">
        {open && (
          <div
            ref={sheet}
            role="menu"
            className="mx-3 mb-2 overflow-hidden rounded-xl border border-line bg-surface shadow-xl"
          >
            {overflow.map((a) => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                className={clsx(
                  'flex w-full items-center gap-3 px-4 py-3 text-left text-sm',
                  a.danger ? 'text-danger' : a.success ? 'text-success' : 'text-ink',
                  'border-b border-line last:border-b-0 active:bg-surface-2',
                )}
                onClick={() => run(a)}
              >
                <span className="shrink-0">
                  {a.loading ? <Loader2 className="size-4 animate-spin" /> : a.icon}
                </span>
                {a.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-stretch border-t border-line bg-surface pb-[env(safe-area-inset-bottom)]">
          {inline.map((a) => (
            <button
              key={a.key}
              type="button"
              className={clsx(
                'flex flex-1 flex-col items-center gap-1 px-1 py-2.5 active:bg-surface-2 disabled:opacity-50',
                a.success ? 'text-success' : 'text-muted',
              )}
              disabled={a.loading}
              title={a.title}
              onClick={() => a.onClick()}
            >
              {a.loading ? <Loader2 className="size-5 animate-spin" /> : a.icon}
              <span className="max-w-full truncate text-[11px] leading-none">{a.label}</span>
            </button>
          ))}

          {overflow.length > 0 && (
            <button
              type="button"
              aria-expanded={open}
              aria-label={`${overflow.length} more actions`}
              className={clsx(
                'flex flex-1 flex-col items-center gap-1 px-1 py-2.5 active:bg-surface-2',
                open ? 'text-primary' : 'text-muted',
              )}
              onClick={() => setOpen((v) => !v)}
            >
              <MoreHorizontal className="size-5" />
              <span className="text-[11px] leading-none">More</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
